<template>
    <v-dialog v-model="show" max-width="1000" persistent>
        <v-card>
            <v-card-title class="error-header">
                <v-icon color="error" class="mr-3" size="24">mdi-alert-circle</v-icon>
                Scan Fehler
                <v-spacer></v-spacer>
                <v-btn icon @click="dismiss">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <!-- Error Icon and Message -->
                <div class="error-content">
                    <div class="error-icon-section">
                        <v-avatar :color="errorData.color" size="64">
                            <v-icon color="white" size="32">{{ errorData.icon }}</v-icon>
                        </v-avatar>
                    </div>
                    
                    <div class="error-details">
                        <h3 class="error-title">{{ errorData.title }}</h3>
                        <p class="error-message">{{ errorData.message }}</p>
                        
                        <!-- Error-specific additional info -->
                        <div v-if="errorData.details" class="error-info">
                            <v-chip 
                                v-for="(detail, key) in errorData.details"
                                :key="key"
                                small
                                outlined
                                class="mr-2 mb-2"
                            >
                                <strong style="margin-right: 8px;">{{ key }}:</strong> {{ detail }}
                            </v-chip>
                        </div>
                    </div>
                </div>

                <!-- Suggested Actions -->
                <div class="suggested-actions" v-if="errorData.suggestions">
                    <h4 class="actions-title">Empfohlene Aktionen:</h4>
                    <ul class="suggestions-list">
                        <li
                            v-for="(suggestion, index) in errorData.suggestions"
                            :key="index"
                            class="suggestion-item"
                        >
                            {{ suggestion }}
                        </li>
                    </ul>
                </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <!-- Action Buttons based on error type -->
                <v-spacer></v-spacer>
                
                <v-btn text @click="dismiss">
                    Schließen
                </v-btn>
                
                <v-btn
                    color="error"
                    @click="reportProblem"
                >
                    <v-icon left>mdi-bug</v-icon>
                    Problem melden
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'ScanErrorModal',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        errorType: {
            type: String,
            required: true
        },
        errorContext: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        errorData() {
            const errorTypes = {
                'wrong-article': {
                    type: 'wrong-article',
                    icon: 'mdi-package-variant-remove',
                    color: 'warning',
                    title: 'Falscher Artikel gescannt',
                    message: 'Der gescannte Artikel gehört nicht zu dieser Bestellung.',
                    details: {
                        'Gescannt': this.errorContext.scannedSku || 'Unbekannt',
                        'Erwartet': this.errorContext.expectedSku || 'Siehe Bestellung'
                    },
                    suggestions: [
                        'Überprüfen Sie den Barcode des Artikels',
                        'Stellen Sie sicher, dass der Artikel zur aktuellen Bestellung gehört',
                        'Scannen Sie den richtigen Artikel für diese Position'
                    ]
                },
                'too-many-items': {
                    type: 'too-many-items',
                    icon: 'mdi-package-variant-plus',
                    color: 'error',
                    title: 'Zu viele Artikel gescannt',
                    message: 'Sie versuchen mehr Artikel zu scannen, als in der Bestellung enthalten sind.',
                    details: {
                        'Bestellung enthält': (this.errorContext.maxItems || 0) + ' Artikel',
                        'Bereits gescannt': (this.errorContext.scannedCount || 0) + ' Artikel'
                    },
                    suggestions: [
                        'Überprüfen Sie die Bestelldetails',
                        'Kontrollieren Sie bereits gescannte Artikel',
                        'Melden Sie das Problem, falls die Bestellung unvollständig ist'
                    ]
                },
                'duplicate-scan': {
                    type: 'duplicate-scan',
                    icon: 'mdi-content-duplicate',
                    color: 'info',
                    title: 'Artikel bereits gescannt',
                    message: 'Dieser Artikel wurde bereits gescannt und verarbeitet.',
                    details: {
                        'Artikel': this.errorContext.duplicateSku || 'Unbekannt',
                        'Zuerst gescannt': this.errorContext.firstScanTime || 'Unbekannt'
                    },
                    suggestions: [
                        'Scannen Sie den nächsten ungescannten Artikel',
                        'Überprüfen Sie die Scan-Historie',
                        'Setzen Sie den Artikel zurück, falls nötig'
                    ]
                },
                'quantity-exceeded': {
                    type: 'quantity-exceeded',
                    icon: 'mdi-numeric-positive-1',
                    color: 'warning',
                    title: 'Maximale Anzahl erreicht',
                    message: 'Alle Einheiten dieses Artikels wurden bereits gescannt.',
                    details: {
                        'Artikel': this.errorContext.itemSku || 'Unbekannt',
                        'Gescannt': this.errorContext.scannedCount || '0',
                        'Erforderlich': this.errorContext.requiredQty || '1'
                    },
                    suggestions: [
                        'Überprüfen Sie, ob Sie den richtigen Artikel scannen',
                        'Scannen Sie den nächsten ungescannten Artikel',
                        'Kontaktieren Sie das Team bei Unstimmigkeiten'
                    ]
                }
            };
            
            return errorTypes[this.errorType] || {
                type: 'unknown',
                icon: 'mdi-help-circle',
                color: 'grey',
                title: 'Unbekannter Fehler',
                message: 'Ein unbekannter Scan-Fehler ist aufgetreten.',
                suggestions: ['Versuchen Sie es erneut', 'Kontaktieren Sie den Support']
            };
        }
    },
    methods: {
        dismiss() {
            this.$emit('close');
        },
        reportProblem() {
            this.$emit('report-problem', {
                errorType: this.errorType,
                errorContext: this.errorContext,
                timestamp: new Date().toISOString(),
                errorData: this.errorData
            });
            this.dismiss();
        }
    }
}
</script>

<style scoped>
.error-header {
    background-color: #f8f9fa;
    color: #333;
    font-weight: 600;
    border-bottom: 1px solid #e0e0e0;
}

.error-content {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    margin-bottom: 24px;
}

.error-icon-section {
    flex-shrink: 0;
}

.error-details {
    flex: 1;
}

.error-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
}

.error-message {
    font-size: 16px;
    color: #666;
    margin: 0 0 16px 0;
    line-height: 1.4;
}

.suggested-actions {
    background-color: #fafafa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    margin-top: 16px;
}

.actions-title {
    font-size: 14px;
    font-weight: 600;
    color: #666;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.suggestions-list {
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
}

.suggestion-item {
    font-size: 14px;
    color: #555;
    margin-bottom: 6px;
    line-height: 1.4;
}

/* Responsive adjustments */
@media (max-width: 600px) {
    .error-content {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }
    
    .suggested-actions {
        padding: 12px;
    }
    
    .suggestions-list {
        padding-left: 16px;
    }
}
</style>